'use client';

import { c as _c } from "react/compiler-runtime";
import './radio-group.scss';
import * as RdxRadioGroup from '@radix-ui/react-radio-group';
import { FC, Fragment, type JSX, PropsWithChildren, ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, BadgeProps } from '../../atoms/badge/badge.tsx';
import { cn } from '../../utils/helpers.ts';
import { Field, FieldErrorMessage, FieldProps } from '../field/field.tsx';
export interface RadioItemProps {
  value: string;
  text?: string;
  disabled?: boolean;
  required?: boolean;
  subText?: string;
  badge?: BadgeProps;
  type?: 'default' | 'other';
  /**
   * These custom radio item elements will be children of the \<label\> element and should be made up of inline elements such as \<span\>. Block elements should not be used.
   *
   * For accessibility, you should not place interactive content such as anchors or buttons in your provided radio item element.
   *
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/label | the Label element on MDN}
   */
  element?: JSX.Element;
  // autoFocus?: boolean;
}
const RadioGroupItem: FC<PropsWithChildren<{
  id: string;
  currentValue: string;
  item: RadioItemProps;
  className?: string;
  isFocused: boolean | undefined;
  altNode?: ReactNode;
}>> = t0 => {
  const $ = _c(16);
  const {
    id,
    currentValue,
    item,
    className,
    isFocused,
    altNode,
    children
  } = t0;
  const initiatedFocus = useRef(false);
  let t1;
  if ($[0] !== currentValue || $[1] !== isFocused || $[2] !== item.value) {
    t1 = node => {
      if (isFocused && !initiatedFocus.current && currentValue === item.value) {
        node.focus();
        initiatedFocus.current = true;
      }
    };
    $[0] = currentValue;
    $[1] = isFocused;
    $[2] = item.value;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = item.text || item.value;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <RdxRadioGroup.Indicator className="RadioGroup__indicator" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children || $[6] !== className || $[7] !== id || $[8] !== item.disabled || $[9] !== item.value || $[10] !== t1 || $[11] !== t2) {
    t4 = <RdxRadioGroup.Item ref={t1} id={id} className={className} value={item.value} disabled={item.disabled} aria-label={t2}>{children}{t3}</RdxRadioGroup.Item>;
    $[5] = children;
    $[6] = className;
    $[7] = id;
    $[8] = item.disabled;
    $[9] = item.value;
    $[10] = t1;
    $[11] = t2;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== altNode || $[14] !== t4) {
    t5 = <>{t4}{altNode}</>;
    $[13] = altNode;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
export interface RadioGroupProps extends Omit<FieldProps, 'children' | 'min' | 'max' | 'minLength' | 'maxLength' | 'valueAsNumber' | 'valueAsDate' | 'pattern'> {
  label?: string;
  variant?: 'default' | 'buttons' | 'boxed' | 'grid';
  orientation?: RdxRadioGroup.RadioGroupProps['orientation'];
  dir?: RdxRadioGroup.RadioGroupProps['dir'];
  loop?: RdxRadioGroup.RadioGroupProps['loop'];
  'aria-label'?: RdxRadioGroup.RadioGroupProps['aria-label'];
  'aria-labelledby'?: RdxRadioGroup.RadioGroupProps['aria-labelledby'];
  'aria-describedby'?: RdxRadioGroup.RadioGroupProps['aria-describedby'];
  'aria-details'?: RdxRadioGroup.RadioGroupProps['aria-details'];
  'aria-errormessage'?: RdxRadioGroup.RadioGroupProps['aria-errormessage'];
  items?: RadioItemProps[];
}
export const RadioGroup: React.FC<RadioGroupProps> = t0 => {
  const $ = _c(36);
  let ariaDescribedBy;
  let ariaDetails;
  let ariaErrorMessage;
  let ariaLabel;
  let ariaLabelledBy;
  let defaultValue;
  let dir;
  let fieldProps;
  let isFocused;
  let items;
  let label;
  let loop;
  let onChange;
  let orientation;
  let t1;
  if ($[0] !== t0) {
    ({
      label,
      variant: t1,
      dir,
      loop,
      onChange,
      orientation,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-describedby": ariaDescribedBy,
      "aria-details": ariaDetails,
      "aria-errormessage": ariaErrorMessage,
      items,
      isFocused,
      defaultValue,
      ...fieldProps
    } = t0);
    $[0] = t0;
    $[1] = ariaDescribedBy;
    $[2] = ariaDetails;
    $[3] = ariaErrorMessage;
    $[4] = ariaLabel;
    $[5] = ariaLabelledBy;
    $[6] = defaultValue;
    $[7] = dir;
    $[8] = fieldProps;
    $[9] = isFocused;
    $[10] = items;
    $[11] = label;
    $[12] = loop;
    $[13] = onChange;
    $[14] = orientation;
    $[15] = t1;
  } else {
    ariaDescribedBy = $[1];
    ariaDetails = $[2];
    ariaErrorMessage = $[3];
    ariaLabel = $[4];
    ariaLabelledBy = $[5];
    defaultValue = $[6];
    dir = $[7];
    fieldProps = $[8];
    isFocused = $[9];
    items = $[10];
    label = $[11];
    loop = $[12];
    onChange = $[13];
    orientation = $[14];
    t1 = $[15];
  }
  const variant = t1 === undefined ? "default" : t1;
  let t2;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["forms"];
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  const {
    t
  } = useTranslation(t2);
  const t3 = fieldProps as FieldProps;
  let t4;
  if ($[17] !== ariaDescribedBy || $[18] !== ariaDetails || $[19] !== ariaErrorMessage || $[20] !== ariaLabel || $[21] !== ariaLabelledBy || $[22] !== defaultValue || $[23] !== dir || $[24] !== isFocused || $[25] !== items || $[26] !== label || $[27] !== loop || $[28] !== onChange || $[29] !== orientation || $[30] !== t || $[31] !== variant) {
    t4 = t5 => {
      const {
        controlProps,
        error,
        formMethods
      } = t5;
      const handleValueChange = value => {
        formMethods?.setValue(controlProps.name as string, value);
        const changeEventData = {
          target: {
            name: controlProps.name,
            value
          }
        };
        if (controlProps.onChange) {
          controlProps.onChange(changeEventData);
        }
        if (!onChange) {
          return;
        }
        onChange(changeEventData);
      };
      const labelId = label ? `label-${controlProps.id}` : undefined;
      const currentValue = controlProps.value ?? formMethods?.getValues(controlProps.name || "") ?? defaultValue ?? "";
      return <>{label && <label id={labelId} className="RadioGroup__label label-xl label-amplified block mb-comfy-xs" htmlFor={controlProps.id}>{label}</label>}<RdxRadioGroup.Root className={cn(`RadioGroup RadioGroup--${variant}`, "flex flex-col", {
          "gap-comfy-sm": variant !== "boxed"
        })} onValueChange={handleValueChange} orientation={orientation} dir={dir} loop={loop} aria-label={ariaLabel} aria-labelledby={labelId || ariaLabelledBy} aria-describedby={ariaDescribedBy} aria-details={ariaDetails} aria-errormessage={ariaErrorMessage} {...currentValue && {
          value: currentValue
        }} {...controlProps.defaultValue && {
          defaultValue: controlProps.defaultValue
        }}>{items?.map(item => {
            const id = `${controlProps.id}-${item.value}`;
            return <Fragment key={id}>{variant === "grid" && item.type === "other" && <div className="RadioGroup__divider-horizontal"><p>{t("radioGroup.or")}</p></div>}{variant !== "boxed" && <label key={id} className={cn("flex items-center", {
                "RadioGroup__item-wrapper label-lg label-moderate": !item?.element,
                "RadioGroup__element-wrapper grow-1": item.element,
                "RadioGroup__element--other": item.type === "other",
                "is-selected": item.type === "other" && item.value === currentValue
              })} htmlFor={id}><RadioGroupItem id={id} currentValue={currentValue} item={item} className={cn("RadioGroup__item", {
                  "label-lg label-potent": !item.element,
                  "RadioGroup__item--element": item.element,
                  "RadioGroup__item--other": item.type === "other"
                })} isFocused={isFocused} altNode={<span aria-hidden={item.type !== "other"} className="RadioGroup__item-label" role="presentation">{item.element || item.text}</span>}><span className={cn("RadioGroup__button-label", {
                    "label-xl label-potent": variant === "grid" && item.type !== "other"
                  })}>{item.text || item.value}{item.subText && <p className={cn("label-mild label-md")}>{item.subText}</p>}</span></RadioGroupItem>{item.badge && <span className="RadioGroup__badge"><Badge {...item.badge} /></span>}</label>}{variant === "boxed" && <div className="RadioGroup--boxed__item" key={id}><RadioGroupItem id={id} currentValue={currentValue} item={item} className="RadioGroup--boxed__item-input" isFocused={isFocused} altNode={<label className="RadioGroup--boxed__item-label relative flex items-center" htmlFor={id}>{item.element || item.text}</label>} /></div>}</Fragment>;
          })}</RdxRadioGroup.Root><FieldErrorMessage error={error} id="" /></>;
    };
    $[17] = ariaDescribedBy;
    $[18] = ariaDetails;
    $[19] = ariaErrorMessage;
    $[20] = ariaLabel;
    $[21] = ariaLabelledBy;
    $[22] = defaultValue;
    $[23] = dir;
    $[24] = isFocused;
    $[25] = items;
    $[26] = label;
    $[27] = loop;
    $[28] = onChange;
    $[29] = orientation;
    $[30] = t;
    $[31] = variant;
    $[32] = t4;
  } else {
    t4 = $[32];
  }
  let t5;
  if ($[33] !== t3 || $[34] !== t4) {
    t5 = <Field {...t3}>{t4}</Field>;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  return t5;
};